import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  X,
  Edit,
  ArrowLeft,
  Building2,
  FileText,
  Upload,
  Plus,
  Trash2,
  Save,
  X as CloseIcon,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { dummyMaterials } from "./Components/MaterialComponents/MaterialTable";

const ViewMaterial = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [material, setMaterial] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});

  // Edit mode states
  const [editFormData, setEditFormData] = useState({
    name: "",
    hsnCode: "",
    photo: null,
  });
  const [editStockEntries, setEditStockEntries] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [colors, setColors] = useState([
    "Red",
    "Blue",
    "Green",
    "Black",
    "White",
    "Navy Blue",
    "Grey",
  ]);
  const [showAddColorModal, setShowAddColorModal] = useState(false);
  const [newColor, setNewColor] = useState("");
  const [colorError, setColorError] = useState("");
  const [lastSelectedUnit, setLastSelectedUnit] = useState("");
  const [showCancelEditDialog, setShowCancelEditDialog] = useState(false);

  // Taka denominations state
  const [showTakaModal, setShowTakaModal] = useState(false);
  const [selectedStockEntry, setSelectedStockEntry] = useState(null);
  const [takaInputFields, setTakaInputFields] = useState({});
  const [newTakaQuantity, setNewTakaQuantity] = useState("");
  const [newTakaValue, setNewTakaValue] = useState("");

  useEffect(() => {
    // Find material from dummy data
    const foundMaterial = dummyMaterials.find(m => m.id === parseInt(id));
    if (foundMaterial) {
      // Add mock stock data for demonstration
      const materialWithStock = {
        ...foundMaterial,
        hsnCode: "5208.52.00", // Mock HSN code
        stockEntries: [
          {
            id: 1,
            color: "White",
            quantity: 500,
            unit: "meters",
            rate: 180,
          },
          {
            id: 2,
            color: "Black",
            quantity: 400,
            unit: "meters",
            rate: 180,
          },
          {
            id: 3,
            color: "Navy Blue",
            quantity: 300,
            unit: "meters",
            rate: 180,
          },
          {
            id: 4,
            color: "Grey",
            quantity: 300,
            unit: "meters",
            rate: 180,
          },
        ],
      };
      setMaterial(materialWithStock);

      // Initialize edit form data
      setEditFormData({
        name: materialWithStock.name,
        hsnCode: materialWithStock.hsnCode,
        photo: null,
      });
      setEditStockEntries(materialWithStock.stockEntries);
      setPhotoPreview(materialWithStock.photo);
    }
  }, [id]);

  const handleBack = () => {
    if (isEditMode) {
      setShowCancelEditDialog(true);
    } else {
      navigate("/production/materials");
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setShowCancelEditDialog(true);
  };

  const handleCancelEditConfirmed = () => {
    setIsEditMode(false);
    setShowCancelEditDialog(false);
    // Reset form data to original values
    setEditFormData({
      name: material.name,
      hsnCode: material.hsnCode,
      photo: null,
    });
    setEditStockEntries(material.stockEntries);
    setPhotoPreview(material.photo);
    setErrors({});
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoChange = e => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData(prev => ({
        ...prev,
        photo: file,
      }));
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setEditFormData(prev => ({
      ...prev,
      photo: null,
    }));
    setPhotoPreview(material.photo); // Reset to original photo
  };

  const handleStockChange = (id, field, value) => {
    setEditStockEntries(entries =>
      entries.map(entry => {
        if (entry.id === id) {
          if (field === "unit") {
            setLastSelectedUnit(value);
          }
          return { ...entry, [field]: value };
        }
        return entry;
      }),
    );
  };

  const addNewColorEntry = () => {
    setEditStockEntries(entries => [
      ...entries,
      {
        id: Math.max(...entries.map(e => e.id)) + 1,
        color: "",
        quantity: "",
        unit: lastSelectedUnit || "meters",
        rate: "",
      },
    ]);
  };

  const removeColorEntry = id => {
    setEditStockEntries(entries => entries.filter(entry => entry.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!editFormData.name) newErrors.name = "Material name is required";
    if (!editFormData.hsnCode) newErrors.hsnCode = "HSN code is required";

    // Validate stock entries
    const stockErrors = editStockEntries.map(entry => {
      const entryErrors = {};
      if (!entry.color) entryErrors.color = "Color is required";
      if (!entry.quantity) entryErrors.quantity = "Quantity is required";
      if (!entry.unit) entryErrors.unit = "Unit is required";
      if (!entry.rate) entryErrors.rate = "Rate is required";
      return entryErrors;
    });

    if (stockErrors.some(error => Object.keys(error).length > 0)) {
      newErrors.stock = stockErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async e => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        setShowSuccessModal(true);
      }, 1000);
    }
  };

  const handleAddColorClick = () => {
    setNewColor("");
    setColorError("");
    setShowAddColorModal(true);
  };

  const handleColorSubmit = () => {
    const trimmedColor = newColor.trim();
    if (!trimmedColor) {
      setColorError("Color name is required");
      return;
    }
    if (colors.includes(trimmedColor)) {
      setColorError("This color already exists");
      return;
    }
    setColors(prev => [...prev, trimmedColor]);
    setShowAddColorModal(false);
    setNewColor("");
  };

  // Taka denominations functions
  const handleAddTakaClick = stockEntry => {
    setSelectedStockEntry(stockEntry);
    setShowTakaModal(true);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleAddTakaDenomination = () => {
    if (!newTakaQuantity || !newTakaValue) return;

    const quantity = parseFloat(newTakaQuantity);
    const value = parseFloat(newTakaValue);

    if (quantity <= 0 || value <= 0) return;

    const entryId = selectedStockEntry.id;
    const currentFields = takaInputFields[entryId] || [];

    // Add the current values as a new field set
    setTakaInputFields(prev => ({
      ...prev,
      [entryId]: [
        ...currentFields,
        {
          quantity: newTakaQuantity,
          value: newTakaValue,
          id: Date.now(),
        },
      ],
    }));

    // Clear the initial fields for next entry
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleRemoveTakaField = (entryId, fieldId) => {
    setTakaInputFields(prev => ({
      ...prev,
      [entryId]: prev[entryId]?.filter(field => field.id !== fieldId) || [],
    }));
  };

  const handleCloseTakaModal = () => {
    setShowTakaModal(false);
    setSelectedStockEntry(null);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const shouldShowTakaButton = unit => {
    return ["meters", "kg", "yards"].includes(unit);
  };

  if (!material) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 mb-4">Loading material details...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            {isEditMode ? "Edit Material" : "Material Details"}
          </h1>
        </div>
        {!isEditMode ? (
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all">
            <Edit size={16} />
            Edit Material
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancelEdit}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] transition-all">
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Material Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-base font-medium text-gray-800 mb-4">
              Material Information
            </h2>
            <div className="flex gap-12">
              <div className="w-[160px]">
                <label className="block text-sm font-medium text-gray-700 mb-2 ml-1">
                  Material Photo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg w-full aspect-square">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload"
                    disabled={!isEditMode}
                  />
                  {photoPreview ? (
                    <div className="relative w-full h-full">
                      <img
                        src={photoPreview}
                        alt="Material preview"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      {isEditMode && (
                        <button
                          onClick={handleRemovePhoto}
                          className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100">
                          <CloseIcon size={16} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <label
                      htmlFor="photo-upload"
                      className={`flex flex-col items-center justify-center cursor-pointer h-full ${
                        !isEditMode ? "pointer-events-none" : ""
                      }`}>
                      <Upload size={20} className="text-gray-400 mb-2" />
                      <span className="text-gray-600 text-xs text-center">
                        Click to upload
                      </span>
                    </label>
                  )}
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-center gap-6">
                <div className="min-w-[400px] max-w-[440px]">
                  <TextField
                    fullWidth
                    label="Material Name"
                    name="name"
                    value={editFormData.name}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    size="small"
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Building2 size={18} className="text-[#5652B0]" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#1F2937",
                        backgroundColor: "#F9FAFB",
                      },
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#6B7280",
                      },
                    }}
                  />
                </div>

                <div className="min-w-[400px] max-w-[440px]">
                  <TextField
                    fullWidth
                    label="HSN Code"
                    name="hsnCode"
                    value={editFormData.hsnCode}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    size="small"
                    error={Boolean(errors.hsnCode)}
                    helperText={errors.hsnCode}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FileText size={18} className="text-[#5652B0]" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#1F2937",
                        backgroundColor: "#F9FAFB",
                      },
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#6B7280",
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Stock Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-medium text-gray-800">
                {isEditMode ? "Edit Stock" : "Stock Information"}
              </h2>
              {isEditMode && (
                <button
                  onClick={addNewColorEntry}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                  <Plus size={16} />
                  Add Color
                </button>
              )}
            </div>

            {isEditMode ? (
              // Edit mode - Form fields
              <div className="flex flex-col gap-4">
                {editStockEntries.map((entry, index) => (
                  <div key={entry.id} className="flex flex-col gap-2">
                    <div className="flex gap-4 items-start">
                      <div className="min-w-[200px]">
                        <FormControl
                          fullWidth
                          size="small"
                          error={errors.stock?.[index]?.color}>
                          <InputLabel>Color</InputLabel>
                          <Select
                            value={entry.color}
                            label="Color"
                            onChange={e =>
                              handleStockChange(
                                entry.id,
                                "color",
                                e.target.value,
                              )
                            }>
                            {colors.map(color => (
                              <MenuItem key={color} value={color}>
                                {color}
                              </MenuItem>
                            ))}
                            <MenuItem
                              onClick={e => {
                                e.preventDefault();
                                handleAddColorClick();
                              }}
                              sx={{
                                color: "#5652B0",
                                borderTop: "1px solid #e0e0e0",
                                marginTop: 1,
                                display: "flex",
                                gap: 1,
                              }}>
                              <Plus size={16} />
                              Add New Color
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </div>

                      <div className="min-w-[120px]">
                        <TextField
                          fullWidth
                          label="Quantity"
                          type="number"
                          value={entry.quantity}
                          onChange={e =>
                            handleStockChange(
                              entry.id,
                              "quantity",
                              e.target.value,
                            )
                          }
                          required
                          size="small"
                          error={errors.stock?.[index]?.quantity}
                          helperText={errors.stock?.[index]?.quantity}
                        />
                      </div>

                      <div className="min-w-[120px]">
                        <FormControl
                          fullWidth
                          size="small"
                          required
                          error={errors.stock?.[index]?.unit}>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            value={entry.unit}
                            label="Unit"
                            onChange={e =>
                              handleStockChange(
                                entry.id,
                                "unit",
                                e.target.value,
                              )
                            }>
                            <MenuItem value="meters">Meters</MenuItem>
                            <MenuItem value="yards">Yards</MenuItem>
                            <MenuItem value="pieces">Pieces</MenuItem>
                            <MenuItem value="kg">Kilograms</MenuItem>
                          </Select>
                        </FormControl>
                      </div>

                      <div className="min-w-[120px]">
                        <TextField
                          fullWidth
                          label="Rate"
                          type="number"
                          value={entry.rate}
                          onChange={e =>
                            handleStockChange(entry.id, "rate", e.target.value)
                          }
                          required
                          size="small"
                          error={errors.stock?.[index]?.rate}
                          helperText={errors.stock?.[index]?.rate}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>

                      {shouldShowTakaButton(entry.unit) && (
                        <div className="min-w-[100px]">
                          <button
                            type="button"
                            onClick={() => handleAddTakaClick(entry)}
                            className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                            {takaInputFields[entry.id]?.length > 0
                              ? "Edit Taka"
                              : "Add Taka"}
                          </button>
                        </div>
                      )}

                      {editStockEntries.length > 1 && (
                        <IconButton
                          onClick={() => removeColorEntry(entry.id)}
                          className="mt-1"
                          size="small">
                          <Trash2 size={18} className="text-red-500" />
                        </IconButton>
                      )}
                    </div>

                    {/* Taka Display Inline */}
                    {takaInputFields[entry.id]?.length > 0 && (
                      <div className="ml-4 flex items-center gap-2">
                        <span className="text-xs text-gray-600 font-medium">
                          Taka:
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {takaInputFields[entry.id].map(field => (
                            <span
                              key={field.id}
                              className="px-2 py-1 bg-gray-100 text-xs font-medium rounded">
                              {field.quantity}x{field.value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // View mode - Table
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "none",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}>
                        Color
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}>
                        Quantity
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}>
                        Unit
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}>
                        Rate (₹)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}>
                        Total Value (₹)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}>
                        Taka
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {material.stockEntries.map(entry => (
                      <TableRow
                        key={entry.id}
                        hover
                        sx={{ "&:hover": { backgroundColor: "#F9FAFB" } }}>
                        <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                          {entry.color}
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                          {entry.quantity.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                          {entry.unit}
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                          ₹{entry.rate.toLocaleString()}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "14px",
                            color: "#4B5563",
                            fontWeight: "500",
                          }}>
                          ₹{(entry.quantity * entry.rate).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: "14px", color: "#4B5563" }}>
                          {shouldShowTakaButton(entry.unit) && (
                            <button
                              onClick={() => handleAddTakaClick(entry)}
                              className={`text-xs transition-colors ${
                                takaInputFields[entry.id]?.length > 0
                                  ? "text-[#5652B0] hover:text-[#4A47A3] font-medium"
                                  : "text-gray-400 hover:text-gray-500"
                              }`}>
                              {takaInputFields[entry.id]?.length > 0
                                ? `View Taka (${
                                    takaInputFields[entry.id].length
                                  })`
                                : "No Taka"}
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        backgroundColor: "#F9FAFB",
                        borderTop: "2px solid #E5E7EB",
                      }}>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                        }}>
                        Total
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                        }}>
                        {material.stockEntries
                          .reduce((sum, entry) => sum + entry.quantity, 0)
                          .toLocaleString()}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                        }}>
                        -
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                        }}>
                        -
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                        }}>
                        ₹
                        {material.stockEntries
                          .reduce(
                            (sum, entry) => sum + entry.quantity * entry.rate,
                            0,
                          )
                          .toLocaleString()}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                        }}>
                        -
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </div>

        {/* Action buttons */}
        {!isEditMode ? (
          <div className="flex items-center justify-end gap-4 mt-4 pb-8">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-5 py-2.5 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
              <ArrowLeft size={18} />
              <span className="font-medium">Back to Materials</span>
            </button>
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-5 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg transition-colors">
              <Edit size={18} />
              <span className="font-medium">Edit Material</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-4 mt-4 pb-8">
            <button
              onClick={handleCancelEdit}
              className="flex items-center gap-2 px-5 py-2.5 text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
              <X size={18} />
              <span className="font-medium">Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg transition-colors">
              {isSubmitting ? (
                <>
                  <CircularProgress size={18} color="inherit" />
                  <span className="font-medium">Saving...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span className="font-medium">Save Material</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Add Color Modal */}
        <Dialog
          open={showAddColorModal}
          onClose={() => setShowAddColorModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "0",
            },
          }}>
          <DialogTitle
            sx={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#1F2937",
              borderBottom: "1px solid #E5E7EB",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            Add New Color
            <IconButton
              size="small"
              onClick={() => setShowAddColorModal(false)}>
              <X size={18} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ padding: "24px" }}>
            <TextField
              autoFocus
              fullWidth
              label="Color Name"
              value={newColor}
              onChange={e => setNewColor(e.target.value)}
              error={Boolean(colorError)}
              helperText={colorError}
              size="small"
            />
          </DialogContent>

          <DialogActions
            sx={{
              padding: "16px 24px 24px",
              justifyContent: "flex-end",
              gap: "12px",
            }}>
            <Button
              onClick={() => setShowAddColorModal(false)}
              sx={{
                color: "rgba(0, 0, 0, 0.6)",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                padding: "8px 16px",
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleColorSubmit}
              variant="contained"
              sx={{
                bgcolor: "#5652B0",
                "&:hover": { bgcolor: "#4A47A3" },
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                padding: "8px 20px",
                boxShadow: "none",
              }}>
              Add Color
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Modal */}
        <Dialog
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setIsEditMode(false);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "0",
            },
          }}>
          <DialogTitle
            sx={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#1F2937",
              borderBottom: "1px solid #E5E7EB",
              padding: "20px 24px",
            }}>
            Success!
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <p className="text-gray-600">
              Material has been updated successfully.
            </p>
          </DialogContent>
          <DialogActions
            sx={{
              padding: "16px 24px 24px",
              justifyContent: "flex-end",
            }}>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                setIsEditMode(false);
              }}
              variant="contained"
              sx={{
                bgcolor: "#5652B0",
                "&:hover": { bgcolor: "#4A47A3" },
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                padding: "8px 20px",
                boxShadow: "none",
              }}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Edit Confirmation Modal */}
        <Dialog
          open={showCancelEditDialog}
          onClose={() => setShowCancelEditDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "0",
            },
          }}>
          <div className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4">
                <X className="w-6 h-6 text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Cancel Editing?
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to cancel editing? All unsaved changes
                will be lost.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelEditDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Keep Editing
                </button>
                <button
                  onClick={handleCancelEditConfirmed}
                  className="px-4 py-2 text-white rounded-lg text-sm font-medium bg-[#5652B0] hover:bg-[#4A47A3]">
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        </Dialog>

        {/* Taka Denominations Modal */}
        <Dialog
          open={showTakaModal}
          onClose={handleCloseTakaModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "0",
            },
          }}>
          <DialogTitle
            sx={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#1F2937",
              borderBottom: "1px solid #E5E7EB",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            {isEditMode ? "Add Taka" : "View Taka"}
            <IconButton size="small" onClick={handleCloseTakaModal}>
              <X size={18} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ padding: "24px" }}>
            {selectedStockEntry && (
              <div className="space-y-6">
                {/* Stock Entry Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">
                    Stock Entry Details
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Color:</span>
                      <span className="ml-2 font-medium">
                        {selectedStockEntry.color}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Quantity:</span>
                      <span className="ml-2 font-medium">
                        {selectedStockEntry.quantity} {selectedStockEntry.unit}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Rate:</span>
                      <span className="ml-2 font-medium">
                        ₹{selectedStockEntry.rate}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Value:</span>
                      <span className="ml-2 font-medium">
                        ₹
                        {(
                          selectedStockEntry.quantity * selectedStockEntry.rate
                        ).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Taka Information */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-4">
                    {isEditMode ? "Add New Taka" : "Taka Information"}
                  </h4>

                  {/* Display Existing Takas */}
                  {takaInputFields[selectedStockEntry.id]?.length > 0 ? (
                    <div>
                      <div className="flex flex-wrap gap-3">
                        {takaInputFields[selectedStockEntry.id].map(
                          (field, index) => (
                            <div
                              key={field.id}
                              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 min-w-[200px]">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {field.quantity}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {selectedStockEntry.unit}
                                </span>
                                <span className="text-gray-400">=</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {field.value}
                                </span>
                                <span className="text-xs text-gray-500">
                                  pieces
                                </span>
                              </div>
                              {isEditMode && (
                                <button
                                  onClick={() =>
                                    handleRemoveTakaField(
                                      selectedStockEntry.id,
                                      field.id,
                                    )
                                  }
                                  className="text-red-500 hover:text-red-700 ml-auto">
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No taka entries found</p>
                      <p className="text-sm">
                        Taka entries will appear here when added
                      </p>
                    </div>
                  )}

                  {/* Add New Taka - Only in Edit Mode */}
                  {isEditMode && (
                    <>
                      {/* Initial Input Fields - At Bottom */}
                      <div className="flex gap-4 items-start mb-4 mt-6">
                        <div className="min-w-[200px]">
                          <TextField
                            fullWidth
                            label="Quantity"
                            type="number"
                            value={newTakaQuantity}
                            onChange={e => setNewTakaQuantity(e.target.value)}
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {selectedStockEntry.unit}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </div>
                        <div className="min-w-[200px]">
                          <TextField
                            fullWidth
                            label="Number of Pieces"
                            type="number"
                            value={newTakaValue}
                            onChange={e => setNewTakaValue(e.target.value)}
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  pieces
                                </InputAdornment>
                              ),
                            }}
                          />
                        </div>
                        <button
                          onClick={handleAddTakaDenomination}
                          disabled={!newTakaQuantity || !newTakaValue}
                          className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed">
                          Add Taka
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              padding: "16px 24px 24px",
              justifyContent: "flex-end",
            }}>
            <Button
              onClick={handleCloseTakaModal}
              sx={{
                color: "rgba(0, 0, 0, 0.6)",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "8px",
                padding: "8px 16px",
              }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default ViewMaterial;
